import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import moment from "moment";
import { forEach, pick, range, extend, omit, fromPairs, map } from "lodash";
import SimpleSchema from "@inspire/data/schemas/SimpleSchema";
import { BoursierOptions, SignupSources, OptionsEclaireur, SkippingYearOccupations, Sexe, FinanceStudies, OptionsEclaireurCMI } from "@inspire/data/static/static";
import { Departments } from "@inspire/data/static/departments";
import { markSchemaFieldsAsRequired } from "@inspire/data/helpers/schemas";
import { StaticHelpers } from "@inspire/data/helpers/static";
import { getFormationTypes } from "@inspire/data/helpers/formations";
function matchYoutubeUrl(url) {
    var p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    if (url.match(p)) {
        return url.match(p)[1];
    }
    return false;
}
var PresentationVideoSchema = new SimpleSchema({
    video: {
        type: String,
        optional: true,
        custom: function custom() {
            if (this.value && typeof this.value === "string") {
                if (!matchYoutubeUrl(this.value)) {
                    return "badVideoUrl";
                }
            }
        }
    }
});
var EclaireurTrainingVideosSchema = new SimpleSchema({
    trainingVideosWatched: {
        type: Array,
        optional: true
    },
    "trainingVideosWatched.$": {
        type: String,
        optional: true
    }
});
var eclaireurGeneralInfoFields = {
    picture: {
        type: String,
        optional: true,
        label: "Photo de profil",
        autoform: {
            type: "image-upload",
            enableCropping: true
        }
    },
    birthDate: {
        optional: true,
        type: Date,
        label: "Date de naissance"
    },
    gender: {
        type: String,
        optional: true,
        label: "Tu es... ?",
        autoform: {
            options: Sexe
        }
    },
    skipExperienceEtudiant: {
        optional: true,
        type: Boolean,
        defaultValue: false
    },
    tagline: {
        optional: true,
        type: String,
        max: 200,
        label: "Une phrase pour te d\xe9crire ?"
    },
    thumbnailUrl: {
        optional: true,
        type: String
    }
};
export var birthDateLimit = {
    birthDate: {
        custom: function custom() {
            if (this.value) {
                var val = moment(this.value);
                var minYear = moment().subtract(30, "years");
                var maxYear = moment().subtract(16, "years");
                if (!val.isValid()) {
                    return "badDate";
                }
                if (val.isBefore(minYear)) {
                    return "isAboveAge";
                }
                if (val.isAfter(maxYear)) {
                    return "isBelowAge";
                }
            }
        }
    }
};
var EclaireurGeneralInfoSchema = new SimpleSchema(eclaireurGeneralInfoFields);
var EclaireurGeneralInfoFormSchema = new SimpleSchema(markSchemaFieldsAsRequired(eclaireurGeneralInfoFields, [
    "firstName",
    "skipExperienceEtudiant",
    "tagline",
    "phone",
    "picture",
    "thumbnailUrl", 
]));
var bacInfoFields = {
    speBac: {
        type: String,
        optional: true,
        label: 'Le cas \xe9ch\xe9ant, quelle \xe9tait la sp\xe9cialisation de ton BAC ? (ex : "section europ\xe9enne", "sections sportives scolaires", etc.)'
    },
    departement: {
        type: String,
        optional: true,
        allowedValues: Object.keys(Departments),
        autoform: {
            options: StaticHelpers.getPrettyDepartments()
        },
        label: "Dans quel d\xe9partement l'as-tu pass\xe9 ?"
    },
    lycee: {
        type: String,
        optional: true,
        label: "Nom du lyc\xe9e",
        custom: function custom() {
            var isAutreLycee = this.field("lyceeId").value === "Autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isAutreLycee || isEtranger) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    lyceeCity: {
        type: String,
        optional: true,
        label: "Ville du lyc\xe9e",
        custom: function custom() {
            var isAutreLycee = this.field("lyceeId").value === "Autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isAutreLycee || isEtranger) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    lyceeId: {
        type: String,
        optional: true,
        label: "Lyc\xe9e"
    },
    avgBac: {
        type: Number,
        optional: true,
        min: 1,
        max: 20,
        autoform: {
            options: fromPairs(map(range(10, 21), function(v) {
                return [
                    v.toString(),
                    v
                ];
            }))
        },
        label: "Moyenne au bac - arrondi \xe0 l'unit\xe9"
    },
    boursierLycee: {
        optional: true,
        type: String,
        allowedValues: Object.keys(BoursierOptions),
        autoform: {
            type: "select-radio",
            options: BoursierOptions
        },
        label: "Etais-tu boursier au lyc\xe9e ?"
    },
    studiesFirstYear: {
        optional: true,
        type: Number,
        label: "Ann\xe9e o\xf9 tu as d\xe9but\xe9 tes \xe9tudes sup\xe9rieures"
    }
};
var BacInfoSchema = new SimpleSchema(bacInfoFields);
var BacInfoFormSchema = new SimpleSchema(extend(markSchemaFieldsAsRequired(omit(bacInfoFields, [
    "lycee",
    "lyceeCity",
    "bac_pro",
    "speBac",
    "filiere_techno", 
])), pick(bacInfoFields, [
    "lycee",
    "lyceeCity",
    "bac_pro",
    "speBac",
    "filiere_techno", 
])));
var SecondFormationSchema = new SimpleSchema({
    departement: {
        type: String,
        optional: true,
        allowedValues: Object.keys(Departments),
        autoform: {
            options: StaticHelpers.getPrettyDepartments()
        },
        label: "Dans quel d\xe9partement \xe9tait ton \xe9tablissement ?",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            if (notSkippingYear && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    etablissementId: {
        type: String,
        optional: true,
        label: "Etablissement",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            var notEtranger = this.field("departement").value !== "A l'\xe9tranger";
            if (notSkippingYear && notEtranger && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    etablissement: {
        type: String,
        optional: true,
        label: "Nom de l'\xe9tablissement",
        custom: function custom() {
            var isAutres = this.field("etablissementId").value === "autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isEtranger || isAutres) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    formationId: {
        type: String,
        optional: true,
        label: "Formation suivie"
    },
    formation: {
        type: String,
        optional: true,
        label: "Nom de ta formation"
    },
    option: {
        type: String,
        optional: true,
        label: "Ton option"
    },
    formationYear: {
        type: Number,
        optional: true,
        label: "Ann\xe9e de la formation",
        autoform: {
            options: [
                {
                    label: "1ere ann\xe9e",
                    value: 1
                },
                {
                    label: "2eme ann\xe9e",
                    value: 2
                },
                {
                    label: "3eme ann\xe9e",
                    value: 3
                },
                {
                    label: "4eme ann\xe9e",
                    value: 4
                },
                {
                    label: "5eme ann\xe9e",
                    value: 5
                },
                {
                    label: "6eme ann\xe9e",
                    value: 6
                },
                {
                    label: "7eme ann\xe9e",
                    value: 7
                },
                {
                    label: "Non applicable",
                    value: ""
                }, 
            ]
        }
    },
    doubleDegree: {
        type: Boolean,
        optional: true,
        label: "Double dipl\xf4me ?"
    },
    codePostal: {
        type: String,
        optional: true,
        label: "Code postal de l'\xe9tablissement"
    },
    ville: {
        type: String,
        optional: true,
        label: "Ville de l'\xe9tablissement",
        custom: function custom() {
            var isAutresEtablissement = this.field("etablissementId").value === "autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isEtranger || isAutresEtablissement) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    pays: {
        type: String,
        optional: true,
        label: "Pays de l'\xe9tablissement",
        custom: function custom() {
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if (isEtranger && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    }
});
var StudyHistoryEntry = {
    // this id gets generated on the client
    id: {
        type: String,
        optional: true
    },
    year: {
        type: Number
    },
    skippingYear: {
        type: Boolean,
        defaultValue: false,
        optional: true
    },
    skippingYearOccupation: {
        type: Array,
        optional: true,
        autoform: {
            noselect: true,
            options: SkippingYearOccupations
        },
        label: "Qu'as tu fais pendant cette ann\xe9e l\xe0 ?"
    },
    "skippingYearOccupation.$": {
        type: String,
        optional: true,
        allowedValues: Object.keys(SkippingYearOccupations)
    },
    skippingYearOther: {
        type: String,
        optional: true,
        label: "Autre occupation"
    },
    typeFormation: {
        type: String,
        optional: true,
        autoform: {
            options: getFormationTypes()
        },
        label: "Type de formation suivie"
    },
    formationId: {
        type: String,
        optional: true,
        label: "Formation suivie",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            var notAutres = this.field("etablissementId").value !== "autres";
            var notEtranger = this.field("departement").value !== "A l'\xe9tranger";
            if (notAutres && notSkippingYear && notEtranger && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    formation: {
        type: String,
        optional: true,
        label: "Nom de ta formation",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            var isAutreEtablissement = this.field("etablissementId").value === "autres";
            var isAutreFormation = this.field("formation").value === "autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if (notSkippingYear && (isEtranger || isAutreFormation || isAutreEtablissement) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    option: {
        type: String,
        optional: true,
        label: "Ton option",
        autoform: {
            type: "select",
            options: OptionsEclaireur
        }
    },
    optionAutre: {
        type: String,
        optional: true,
        label: "Libell\xe9 de l'option autre",
        max: 100
    },
    optionMentionCMI: {
        type: String,
        optional: true,
        label: "Mention CMI",
        allowedValues: Object.values(OptionsEclaireurCMI)
    },
    departement: {
        type: String,
        optional: true,
        allowedValues: Object.keys(Departments),
        autoform: {
            options: StaticHelpers.getPrettyDepartments()
        },
        label: "Dans quel d\xe9partement \xe9tait ton \xe9tablissement ?",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            if (notSkippingYear && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    etablissement: {
        type: String,
        optional: true,
        label: "Nom de l'\xe9tablissement",
        custom: function custom() {
            var isAutres = this.field("etablissementId").value === "autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isEtranger || isAutres) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    etablissementId: {
        type: String,
        optional: true,
        label: "Etablissement",
        custom: function custom() {
            var notSkippingYear = this.field("skippingYear").value === false;
            var notEtranger = this.field("departement").value !== "A l'\xe9tranger";
            if (notSkippingYear && notEtranger && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    codePostal: {
        type: String,
        optional: true,
        label: "Code postal de l'\xe9tablissement"
    },
    ville: {
        type: String,
        optional: true,
        label: "Ville de l'\xe9tablissement",
        custom: function custom() {
            var isAutresEtablissement = this.field("etablissementId").value === "autres";
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if ((isEtranger || isAutresEtablissement) && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    pays: {
        type: String,
        optional: true,
        label: "Pays de l'\xe9tablissement",
        custom: function custom() {
            var isEtranger = this.field("departement").value === "A l'\xe9tranger";
            if (isEtranger && !this.isSet && (!this.operator || this.value === null || this.value === "")) {
                return "required";
            }
        }
    },
    formationYear: {
        type: Number,
        optional: true,
        label: "Ann\xe9e de la formation",
        autoform: {
            options: [
                {
                    label: "1ere ann\xe9e",
                    value: 1
                },
                {
                    label: "2eme ann\xe9e",
                    value: 2
                },
                {
                    label: "3eme ann\xe9e",
                    value: 3
                },
                {
                    label: "4eme ann\xe9e",
                    value: 4
                },
                {
                    label: "5eme ann\xe9e",
                    value: 5
                },
                {
                    label: "6eme ann\xe9e",
                    value: 6
                },
                {
                    label: "7eme ann\xe9e",
                    value: 7
                },
                {
                    label: "Non applicable",
                    value: ""
                }, 
            ]
        }
    },
    idEtablissementOnisep: {
        type: String,
        optional: true
    },
    secondFormation: {
        type: SecondFormationSchema,
        label: "Deuxi\xe8me dipl\xf4me",
        optional: true
    }
};
var StudyHistoryEntrySchema = new SimpleSchema(StudyHistoryEntry);
var StudyHistorySchema = new SimpleSchema({
    studies: {
        type: Array,
        optional: true
    },
    "studies.$": {
        type: StudyHistoryEntrySchema,
        optional: true
    }
});
var studyTestimonials = {
    // a list of ids of entries from the studies field
    studyTestimonials: {
        type: Array,
        optional: true,
        autoform: {
            noselect: true
        },
        label: "De quelle(s) formations suivies en Bac+1 voudrais-tu t\xe9moigner ?"
    },
    "studyTestimonials.$": {
        type: String,
        optional: true
    }
};
var StudyTestimonialsSchema = new SimpleSchema(studyTestimonials);
var parcoursdEtudesFields = {
    recommendBac1: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "D\xe9conseillerais-tu pour une raison particuli\xe8re ton bac +1" + " \xe0 un lyc\xe9en qui a le m\xeame profil que toi ?"
    },
    noRecommendBac1Explanation: {
        type: String,
        optional: true,
        label: "Peux-tu nous expliquer pourquoi ?"
    },
    reorientationBac1: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "T'es tu r\xe9orient\xe9 vers un autre bac +1 apr\xe8s un an ?"
    },
    reorientationBac1Explanation: {
        optional: true,
        type: String,
        label: "Peux-tu nous dire pourquoi tu t'es r\xe9orient\xe9 ?"
    }
};
var EclaireurParcoursdEtudesSchema = new SimpleSchema(parcoursdEtudesFields);
var EclaireurParcoursdEtudesFormSchema = new SimpleSchema(markSchemaFieldsAsRequired(parcoursdEtudesFields, [
    "noRecommendBac1Explanation",
    "reorientationBac1Explanation", 
]));
var experienceEtudiantFields = {
    knewNobodyBacP1: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "As-tu d\xe9j\xe0 \xe9tudi\xe9 dans une formation o\xf9 tu ne connaissais personne ?"
    },
    livedWithFamilyBacP1: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "Logeais-tu chez tes parents pendant au moins une partie de tes \xe9tudes ?"
    },
    logementEtudiant: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "As-tu b\xe9n\xe9fici\xe9 d'un logement \xe9tudiant (type crous) ?"
    },
    studiedAbroad: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "As-tu d\xe9j\xe0 \xe9tudi\xe9 \xe0 l’\xe9tranger ?"
    },
    apprentissage: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "As-tu d\xe9j\xe0 \xe9tudi\xe9 en alternance (contrat d’apprentissage ou de professionnalisation) ?"
    },
    sourceOfFinanceStudies: {
        type: Array,
        optional: true,
        autoform: {
            noselect: true,
            options: FinanceStudies
        },
        label: "As-tu financ\xe9 tes \xe9tudes par le(s) moyen(s) suivant(s) ?"
    },
    "sourceOfFinanceStudies.$": {
        type: String,
        optional: true
    },
    willReviewCV: {
        type: Boolean,
        optional: true,
        autoform: {
            type: "boolean-radios",
            trueLabel: "Oui",
            falseLabel: "Non"
        },
        label: "Accepterais-tu de relire les CV et les lettres de motivations* que les lyc\xe9ens doivent \xe9crire sur Parcoursup ?"
    },
    signupSource: {
        type: Array,
        optional: true,
        autoform: {
            noselect: true,
            options: SignupSources
        },
        minCount: 1,
        label: "Comment as-tu entendu parler de nous ?"
    },
    "signupSource.$": {
        type: String,
        optional: true
    },
    signupSourceOther: {
        type: String,
        optional: true,
        label: "Quelle autre source ?"
    }
};
var ExperienceEtudiantSchema = new SimpleSchema(experienceEtudiantFields);
var ExperienceEtudiantFormSchema = new SimpleSchema(markSchemaFieldsAsRequired(experienceEtudiantFields, [
    "experienceFields",
    "sourceOfFinanceStudies",
    "signupSourceEntreprise",
    "signupSourceOther", 
]));
var EclaireurQuestions = [
    {
        name: "moments",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "Quels ont \xe9t\xe9 les meilleurs moments de tes premi\xe8res ann\xe9es d'\xe9tudes ?"
        }
    },
    {
        name: "positif",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "Est-ce qu'il y a des aspects positifs de ta formation auxquels tu ne t'attendais pas, " + "qui ont \xe9t\xe9 des bonnes surprises \xe0 la rentr\xe9e ?"
        }
    },
    {
        name: "hardTimes",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "Quels ont \xe9t\xe9 les moments difficiles ? Comment les as-tu d\xe9pass\xe9s ?"
        }
    },
    {
        name: "accomodation",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "O\xf9 as-tu v\xe9cu pendant tes premi\xe8res ann\xe9es d’\xe9tudes ?"
        }
    },
    {
        name: "options",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "As-tu choisi des options particuli\xe8res dans le cadre de tes" + " premi\xe8res ann\xe9es d'\xe9tudes (langues, arts...) ?"
        }
    },
    {
        name: "groupe",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "Travaillais-tu souvent en groupes pendant tes premi\xe8res ann\xe9es d'\xe9tudes ?"
        }
    },
    {
        name: "ambiance",
        question: {
            type: String,
            max: 600,
            optional: true,
            label: "Peux-tu dire deux mots sur l'ambiance en cours pendant tes premi\xe8res ann\xe9es d'\xe9tudes : " + "ta classe, en amphith\xe9\xe2tre, dans ton groupe de TD ?"
        }
    }, 
];
// create Schema used for eclaireur profile
var eclaireurQuestionFields = {};
forEach(EclaireurQuestions, function(q) {
    var fieldName = [
        "eclaireurQuestions",
        q.name
    ].join(".");
    eclaireurQuestionFields[fieldName] = {
        type: String,
        optional: true
    };
});
var EclaireurQuestionsSchema = new SimpleSchema(_object_spread({
    eclaireurQuestions: {
        type: Object,
        optional: true
    }
}, eclaireurQuestionFields));
var EclaireurPisteSchema = new SimpleSchema({
    pisteIds: {
        type: Array,
        optional: true,
        label: "Pistes d'\xe9tude"
    },
    "pisteIds.$": {
        type: String,
        optional: true
    },
    pisteName: {
        type: String,
        optional: true
    }
});
var EclaireurProfileSchema = new SimpleSchema()// eclaireurs: presentation video
.extend(PresentationVideoSchema)// training video flags
.extend(EclaireurTrainingVideosSchema)// eclaireurs: general info (phone); age etc)
.extend(EclaireurGeneralInfoSchema)// eclaireurs: bac info
.extend(BacInfoSchema)// eclaireurs: history of studies after bac
.extend(StudyHistorySchema)// eclaireurs: parcours d'etudes
.extend(EclaireurParcoursdEtudesSchema)// eclaireurs: testimonials
.extend(StudyTestimonialsSchema)// eclaireurs: student experience
.extend(ExperienceEtudiantSchema)// eclaireurs: profile question
.extend(EclaireurQuestionsSchema)// eclaireur-piste matching
.extend(EclaireurPisteSchema);
export { PresentationVideoSchema, EclaireurTrainingVideosSchema, EclaireurGeneralInfoSchema, EclaireurGeneralInfoFormSchema, BacInfoSchema, BacInfoFormSchema, StudyHistorySchema, EclaireurParcoursdEtudesSchema, StudyTestimonialsSchema, ExperienceEtudiantSchema, ExperienceEtudiantFormSchema, EclaireurQuestions, EclaireurQuestionsSchema, EclaireurPisteSchema, EclaireurParcoursdEtudesFormSchema, StudyHistoryEntrySchema, EclaireurProfileSchema, SecondFormationSchema };
